import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { paidVoucherAPI, branchAPI, organizationAPI } from '../../services/api';
import { useBranchFilter } from '../../contexts/BranchFilterContext';
import authService from '../../services/authService';
import ErrorModal from '../Common/ErrorModal';
import PaidVoucherPrint from './PaidVoucherPrint';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './PaidVoucherCreate.css';
import './PaidVoucherPrint.css';

const PaidVoucherView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedBranchId } = useBranchFilter();
  const [paidVoucher, setPaidVoucher] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showBranchModal, setShowBranchModal] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    loadPaidVoucher();
    if (selectedBranchId) {
      loadOrganizationData();
    }
  }, [id, selectedBranchId]);

  const loadPaidVoucher = async () => {
    try {
      setLoading(true);
      const response = await paidVoucherAPI.getById(id);
      const data = response.data;
      setPaidVoucher({
        ...data,
        voucherDate: data.voucherDate ? new Date(data.voucherDate).toISOString().split('T')[0] : '',
        amount: data.amount?.toString() || '',
        isActive: data.isActive ? 'true' : 'false',
      });
    } catch (err) {
      setErrorMessage(t('paidVoucher.view.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizationData = async () => {
    try {
      // Get branch data
      const branchResponse = await branchAPI.getById(selectedBranchId);
      const branch = branchResponse.data;
      
      // Check if organization is included in branch response
      if (branch && (branch.organization || branch.Organization)) {
        const org = branch.organization || branch.Organization;
        if (org && (org.id || org.Id)) {
          // Organization is already included, use it
          setOrganization(org);
        } else if (branch.organizationId || branch.OrganizationId) {
          // Get organization data separately
          const orgId = branch.organizationId || branch.OrganizationId;
          const orgResponse = await organizationAPI.getById(orgId);
          setOrganization(orgResponse.data);
        }
      } else if (branch && (branch.organizationId || branch.OrganizationId)) {
        // Get organization data separately
        const orgId = branch.organizationId || branch.OrganizationId;
        const orgResponse = await organizationAPI.getById(orgId);
        setOrganization(orgResponse.data);
      }
    } catch (err) {
      console.error('Error loading organization data:', err);
    }
  };

  const handlePrint = async () => {
    if (!printRef.current || !paidVoucher) return;

    // Check if branch is selected
    if (!selectedBranchId) {
      setShowBranchModal(true);
      return;
    }

    // Ensure organization data is loaded
    if (!organization) {
      await loadOrganizationData();
      if (!organization) {
        setErrorMessage(t('paidVoucher.view.messages.loadError'));
        setShowErrorModal(true);
        return;
      }
    }

    try {
      // Show loading state
      const loadingElement = document.createElement('div');
      loadingElement.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0,0,0,0.8);
        color: white;
        padding: 20px 40px;
        border-radius: 8px;
        z-index: 999999;
        font-size: 16px;
      `;
      loadingElement.textContent = 'Generating PDF...';
      document.body.appendChild(loadingElement);

      // Get the print container
      const printContainer = printRef.current;
      
      // Store original styles
      const originalStyles = {
        display: printContainer.style.display,
        position: printContainer.style.position,
        left: printContainer.style.left,
        top: printContainer.style.top,
        width: printContainer.style.width,
        backgroundColor: printContainer.style.backgroundColor,
        visibility: printContainer.style.visibility,
        zIndex: printContainer.style.zIndex,
      };

      // Temporarily show and position the print container
      printContainer.style.display = 'block';
      printContainer.style.position = 'fixed';
      printContainer.style.left = '0';
      printContainer.style.top = '0';
      printContainer.style.width = '210mm';
      printContainer.style.backgroundColor = 'white';
      printContainer.style.visibility = 'visible';
      printContainer.style.zIndex = '999998';

      // Preload logo image and convert to data URL
      let logoDataUrl = null;
      console.log('Organization data:', organization);
      if (organization && (organization.logo || organization.Logo)) {
        const logoPath = organization.logo || organization.Logo;
        console.log('Logo path:', logoPath);
        try {
          const logoPath = organization.logo || organization.Logo;
          const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nadeedapi.urapp4u.com/api';
          const baseUrl = API_BASE_URL.replace('/api', '');
          const fullLogoUrl = `${baseUrl}${logoPath}`;
          
          // Fetch image as blob with authentication
          const token = authService.getToken();
          try {
            const response = await fetch(fullLogoUrl, {
              method: 'GET',
              credentials: 'include',
              headers: token ? {
                'Authorization': `Bearer ${token}`
              } : {}
            });
            
            if (response.ok) {
              const blob = await response.blob();
              logoDataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = () => {
                  console.warn('Failed to read logo blob');
                  resolve(null);
                };
                reader.readAsDataURL(blob);
              });
            } else {
              console.warn('Failed to fetch logo:', response.status);
            }
          } catch (fetchErr) {
            console.warn('Failed to fetch logo as blob, trying direct image load:', fetchErr);
            
            // Fallback: try direct image load
            logoDataUrl = await new Promise((resolve) => {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                try {
                  const canvas = document.createElement('canvas');
                  canvas.width = img.width;
                  canvas.height = img.height;
                  const ctx = canvas.getContext('2d');
                  ctx.drawImage(img, 0, 0);
                  resolve(canvas.toDataURL('image/png'));
                } catch (err) {
                  console.warn('Failed to convert logo to canvas:', err);
                  resolve(null);
                }
              };
              img.onerror = () => {
                console.warn('Failed to load logo image');
                resolve(null);
              };
              img.src = fullLogoUrl;
            });
          }
        } catch (err) {
          console.warn('Failed to preload logo:', err);
        }
      }

      // Replace logo image src with data URL in the actual DOM before capture
      if (logoDataUrl) {
        const logoImg = printContainer.querySelector('.voucher-logo-img');
        if (logoImg) {
          logoImg.src = logoDataUrl;
          // Wait for image to load
          await new Promise((resolve) => {
            if (logoImg.complete) {
              resolve();
            } else {
              logoImg.onload = () => resolve();
              logoImg.onerror = () => resolve(); // Continue even if image fails
              setTimeout(() => resolve(), 2000); // Timeout after 2 seconds
            }
          });
        }
      } else {
        // Wait for image in print container to load (if using original URL)
        const logoImg = printContainer.querySelector('.voucher-logo-img');
        if (logoImg && logoImg.src) {
          await new Promise((resolve) => {
            if (logoImg.complete) {
              resolve();
            } else {
              logoImg.onload = () => resolve();
              logoImg.onerror = () => resolve(); // Continue even if image fails
              setTimeout(() => resolve(), 2000); // Timeout after 2 seconds
            }
          });
        }
      }

      // Wait for styles to apply and content to render
      await new Promise(resolve => setTimeout(resolve, 500));

      // Capture the element as canvas with high quality
      const canvas = await html2canvas(printContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
        backgroundColor: '#ffffff',
        width: printContainer.scrollWidth,
        height: printContainer.scrollHeight,
        windowWidth: printContainer.scrollWidth,
        windowHeight: printContainer.scrollHeight,
        onclone: (clonedDoc) => {
          // Ensure all styles are applied in the cloned document
          const clonedElement = clonedDoc.querySelector('.voucher-print-container');
          if (clonedElement) {
            clonedElement.style.display = 'block';
            clonedElement.style.backgroundColor = 'white';
          }
          
          // Replace logo image with data URL if available
          if (logoDataUrl) {
            const clonedLogoImg = clonedDoc.querySelector('.voucher-logo-img');
            if (clonedLogoImg) {
              clonedLogoImg.src = logoDataUrl;
              clonedLogoImg.crossOrigin = 'anonymous';
              clonedLogoImg.style.display = 'block';
              clonedLogoImg.style.visibility = 'visible';
              clonedLogoImg.style.width = '50px';
              clonedLogoImg.style.height = '50px';
              clonedLogoImg.style.borderRadius = '50%';
              clonedLogoImg.style.objectFit = 'contain';
              clonedLogoImg.style.margin = '0 auto 5px';
              
              // Hide fallback logo div
              const clonedLogoDiv = clonedDoc.querySelector('.voucher-logo');
              if (clonedLogoDiv) {
                clonedLogoDiv.style.display = 'none';
              }
            }
          } else {
            // If no logo data URL, hide image and show fallback
            const clonedLogoImg = clonedDoc.querySelector('.voucher-logo-img');
            if (clonedLogoImg) {
              clonedLogoImg.style.display = 'none';
            }
            const clonedLogoDiv = clonedDoc.querySelector('.voucher-logo');
            if (clonedLogoDiv) {
              clonedLogoDiv.style.display = 'flex';
            }
          }
        },
      });

      // Restore original styles
      Object.keys(originalStyles).forEach(key => {
        printContainer.style[key] = originalStyles[key] || '';
      });

      // Remove loading element
      document.body.removeChild(loadingElement);

      // Calculate PDF dimensions
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // A4 dimensions in mm (at 96 DPI, 1mm = 3.779527559 pixels)
      const mmToPx = 3.779527559;
      const pdfWidthMM = 210;
      const pdfHeightMM = 297;
      
      // Apply 10mm margins
      const marginMM = 10;
      const contentWidthMM = pdfWidthMM - (marginMM * 2);
      const contentHeightMM = pdfHeightMM - (marginMM * 2);
      
      // Convert canvas dimensions to mm
      const imgWidthMM = imgWidth / mmToPx;
      const imgHeightMM = imgHeight / mmToPx;
      
      // Calculate scaling to fit within margins
      const widthRatio = contentWidthMM / imgWidthMM;
      const heightRatio = contentHeightMM / imgHeightMM;
      const ratio = Math.min(widthRatio, heightRatio);
      
      const scaledWidthMM = imgWidthMM * ratio;
      const scaledHeightMM = imgHeightMM * ratio;
      
      // Center the image on the page with margins
      const xOffset = marginMM + (contentWidthMM - scaledWidthMM) / 2;
      const yOffset = marginMM + (contentHeightMM - scaledHeightMM) / 2;
      
      // Create PDF
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      
      // Add image to PDF with margins
      const imgData = canvas.toDataURL('image/png', 1.0);
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, scaledWidthMM, scaledHeightMM);
      
      // Open PDF in new tab instead of downloading
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');

      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 100);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setErrorMessage('Failed to generate PDF. Please try again.');
      setShowErrorModal(true);
      const loadingElement = document.querySelector('div[style*="position: fixed"]');
      if (loadingElement && loadingElement.textContent === 'Generating PDF...') {
        document.body.removeChild(loadingElement);
      }
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!paidVoucher) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">{t('paidVoucher.list.messages.noData')}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="no-print">
        <div className="page-header-actions">
          <button className="btn-back" onClick={() => navigate('/paid-voucher')}>
            <i className="fas fa-arrow-left"></i> {t('paidVoucher.view.backToList')}
          </button>
          <button className="btn-edit" onClick={() => navigate(`/paid-voucher/edit/${id}`)}>
            <i className="fas fa-edit"></i> {t('paidVoucher.view.edit')}
          </button>
          <button className="btn-print" onClick={handlePrint}>
            <i className="fas fa-print"></i> {t('common.print')}
          </button>
        </div>
      </div>

      {/* Print Layout - Only visible when printing */}
      <PaidVoucherPrint ref={printRef} paidVoucher={paidVoucher} organization={organization} />

      {/* Regular View - Hidden when printing */}
      <div className="no-print">
        <div className="form-container">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-hashtag"></i> {t('paidVoucher.view.fields.voucherNumber')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-hashtag input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  value={paidVoucher.voucherNumber}
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('paidVoucher.view.fields.voucherDate')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className="form-input"
                  value={paidVoucher.voucherDate}
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('paidVoucher.view.fields.paidTo')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  value={paidVoucher.paidTo || '-'}
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-dollar-sign"></i> {t('paidVoucher.view.fields.amount')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-dollar-sign input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  value={paidVoucher.amount}
                  readOnly
                  disabled
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-alt"></i> {t('paidVoucher.view.fields.amountInWords')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-alt input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  value={paidVoucher.amountInWords || '-'}
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-credit-card"></i> {t('paidVoucher.view.fields.paymentMethod')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-credit-card input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  value={paidVoucher.paymentMethod || '-'}
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-invoice"></i> {t('paidVoucher.view.fields.chequeNumber')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-invoice input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  value={paidVoucher.chequeNumber || '-'}
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-university"></i> {t('paidVoucher.view.fields.bank')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-university input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  value={paidVoucher.bank || '-'}
                  readOnly
                  disabled
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-info-circle"></i> {t('paidVoucher.view.fields.purpose')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-info-circle input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  value={paidVoucher.purpose || '-'}
                  readOnly
                  disabled
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-check-circle"></i> {t('paidVoucher.view.fields.isActive')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-check-circle input-icon"></i>
                <select
                  className="form-input"
                  value={paidVoucher.isActive}
                  disabled
                >
                  <option value="true">{t('common.yes')}</option>
                  <option value="false">{t('common.no')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />

      <ErrorModal
        show={showBranchModal}
        message={t('paidVoucher.view.messages.selectBranch', 'Please select organization branch')}
        onClose={() => setShowBranchModal(false)}
      />
    </div>
  );
};

export default PaidVoucherView;
